import React, { useCallback, useImperativeHandle, useState, forwardRef } from 'react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { DebouncedOnChangePlugin } from '../DebouncedOnChangePlugin/DebouncedOnChangePlugin';
import { NodeEventPlugin } from '@lexical/react/LexicalNodeEventPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';

import { CLEAR_EDITOR_COMMAND, EditorState, LexicalEditor } from 'lexical';

import { toRichText } from '../ast';
import { flexibleTextToEditorState } from '../flexibleTextAst';
import { ContentEditable } from '../ContentEditable/ContentEditable';
import { OnBlurPlugin } from '../OnBlurPlugin/OnBlurPlugin';
import { OnFocusPlugin } from '../OnFocusPlugin/OnFocusPlugin';
import { PolyfillShadowDomSelectionPlugin } from '../PolyfillShadowDomSelectionPlugin/PolyfillShadowDomSelectionPlugin';
import { SUPPORTED_MARKDOWN_TRANSFORMERS, useLexicalConfig } from './lexicalConfig';
import { RichText } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import { FlexibleTextContent } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { OnKeyDownPlugin } from '@mutiny-pkg/bin-ui/src/richtext/OnKeyDownPlugin/OnKeyDownPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ExternalStateSynchronizerPlugin } from '../ExternalStateSynchronizerPlugin/ExternalStateSynchronizerPlugin';
import { EditablePlugin } from '../EditablePlugin/EditablePlugin';
import { LinkNode } from '@lexical/link';
import { FloatingMenuPlugin } from '../FloatingMenuPlugin/FloatingMenuPlugin';
import { FloatingMenu } from '../FloatingMenuPlugin/FloatingMenu';
import { MentionItem, MentionsPlugin } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsPlugin';
import { isValidUrl, MATCHERS } from '@mutiny-pkg/bin-ui/src/utils/url';
import { ExtendedTextPlugin } from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextPlugin';
import { PlainPastePlugin } from '@mutiny-pkg/bin-ui/src/richtext/PlainPastePlugin/PlainPastePlugin';
import { RichInputVariant } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import { LookupItem, LookupPlugin } from '../LookupPlugin/LookupPlugin';
import { LexicalThemeColors } from '../types';

export type WithMentionItems =
  | {
      mentionItems: MentionItem[];
      onMentionMenuOpen: () => void;
      mentionItemsEmptyMessage?: string;
    }
  | {
      mentionItems?: undefined;
      onMentionMenuOpen?: undefined;
      mentionItemsEmptyMessage?: undefined;
    };

export type RichInputHandle = {
  focus: () => void;
  clear: () => void;
};

type RichInputProps = {
  ariaLabel?: string;
  value?: FlexibleTextContent | RichText | string;
  children?: React.ReactElement<typeof ContentEditable>;
  lookupItems?: LookupItem[];
  editable?: boolean;
  autoFocus?: boolean;
  variant?: RichInputVariant;
  enableFloatingMenu?: boolean;
  enableTextAlign?: boolean;
  enableExtendedTextFormatting?: boolean;
  debounced?: boolean;
  onBlur?: (ev: Event, value: RichText) => void;
  onFocus?: (ev: Event, value: RichText) => void;
  onChange?: (value: RichText) => void;
  onError?: (error: Error) => void;
  noDisplayColor?: boolean;
  themeColors?: LexicalThemeColors;
  onKeyDown?: Partial<{ [key: string]: (ev: React.KeyboardEvent, value: RichText) => boolean }>;
  onColorChange?: (color: string, isFullSelection: boolean, type: 'text' | 'highlight') => boolean | void;
} & WithMentionItems;

const validateUrl = (url: string) => {
  return isValidUrl(url, { allowAnyProtocol: true });
};

const ImperativeHandlePlugin = ({ imperativeRef }: { imperativeRef: React.Ref<RichInputHandle> }) => {
  const [editor] = useLexicalComposerContext();

  useImperativeHandle(
    imperativeRef,
    () => ({
      focus: () => {
        try {
          editor.focus();
          return;
        } catch {
          // Fallback to DOM focus if editor focus fails
        }
        const root = editor.getRootElement();
        if (!root) {
          return;
        }
        try {
          root.focus({ preventScroll: true } as FocusOptions);
        } catch {
          root.focus();
        }
      },
      clear: () => {
        editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      },
    }),
    [editor],
  );

  return null;
};

const RichInput = forwardRef<RichInputHandle, RichInputProps>(function RichInput(
  {
    ariaLabel,
    value,
    mentionItems,
    onMentionMenuOpen,
    mentionItemsEmptyMessage,
    lookupItems,
    children,
    editable,
    autoFocus,
    variant,
    enableFloatingMenu = true,
    enableTextAlign,
    enableExtendedTextFormatting,
    debounced = false,
    noDisplayColor,
    themeColors,
    onBlur,
    onFocus,
    onChange,
    onError,
    onKeyDown,
    onColorChange,
  }: RichInputProps,
  ref,
) {
  // Lexical internally memoizes initialValue. Subsequent editorState changes are handled
  // by the useEffect below.
  const initialConfig = useLexicalConfig({
    variant,
    editorState: JSON.stringify(flexibleTextToEditorState(value ?? '')),
    editable,
  });

  const _onBlur = useCallback(
    (ev: Event, editorState: EditorState, editor: LexicalEditor) => {
      try {
        if (!onBlur) {
          return;
        }

        // Don't call onBlur if focus is moving to toolbar/menu elements
        if (ev instanceof FocusEvent) {
          const relatedTarget = ev.relatedTarget as HTMLElement | null;
          const target = ev.target as HTMLElement | null;

          // Check if blur is coming from within the link editor menu
          if (target && target.closest('.link-editor')) {
            return;
          }

          if (relatedTarget) {
            const isToolbarClick =
              relatedTarget.closest('[role="dialog"]') ||
              relatedTarget.closest('[data-floating-menu]') ||
              relatedTarget.closest('[data-link-menu-open]') ||
              relatedTarget.closest('.link-editor') ||
              relatedTarget.closest('button[tabindex="-1"]') ||
              relatedTarget.closest('[data-radix-popper-content-wrapper]');

            if (isToolbarClick) {
              return;
            }
          }
        }

        const richText = toRichText(editorState);
        onBlur?.(ev, richText);
      } catch (error: unknown) {
        if (error instanceof Error) {
          editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
          onError?.(error);
        }
      }
    },
    [onBlur, onError],
  );

  const _onFocus = useCallback(
    (ev: Event, editorState: EditorState, editor: LexicalEditor) => {
      try {
        if (!onFocus) {
          return;
        }
        const richText = toRichText(editorState);
        onFocus?.(ev, richText);
      } catch (error: unknown) {
        if (error instanceof Error) {
          editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
          onError?.(error);
        }
      }
    },
    [onFocus, onError],
  );

  const _onChange = useCallback(
    (editorState: EditorState, editor?: LexicalEditor) => {
      try {
        if (!onChange) {
          return;
        }
        const richText = toRichText(editorState);
        onChange?.(richText);
      } catch (error) {
        if (error instanceof Error && editor) {
          editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
          onError?.(error);
        }
      }
    },
    [onChange, onError],
  );

  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const _mentionItems = React.useMemo(() => mentionItems ?? [], [mentionItems]);

  const handleMentionMenuOpen = useCallback(
    (state: boolean) => {
      setMentionMenuOpen(state);
      onMentionMenuOpen?.();
    },
    [setMentionMenuOpen, onMentionMenuOpen],
  );

  // Recreate the function closures with the editor state so that we have access
  // to the rich text value when firing one of the callbacks
  const _onKeyDown = React.useMemo<
    Partial<{ [key: string]: (ev: React.KeyboardEvent, editorState: EditorState) => boolean }>
  >(
    () =>
      onKeyDown
        ? Object.fromEntries(
            Object.entries(onKeyDown).map(([key, listener]) => [
              key,
              (ev, editorState) => {
                return listener?.(ev, toRichText(editorState)) ?? false;
              },
            ]),
          )
        : {},
    [onKeyDown],
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <ImperativeHandlePlugin imperativeRef={ref} />
      <ExternalStateSynchronizerPlugin value={value} />
      <EditablePlugin editable={editable} />
      {/* Infra */}
      <PolyfillShadowDomSelectionPlugin />
      <LinkPlugin validateUrl={validateUrl} />
      <AutoLinkPlugin matchers={MATCHERS} />
      <ClearEditorPlugin />

      {/* Nodes */}
      <RichTextPlugin
        contentEditable={children ?? <ContentEditable ariaLabel={ariaLabel} />}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <ListPlugin />

      {/* Prevent Link from clickable */}
      <NodeEventPlugin
        nodeType={LinkNode}
        eventType="click"
        eventListener={(e: Event) => {
          e.preventDefault();
        }}
      />

      {lookupItems && <LookupPlugin items={lookupItems} />}
      <MentionsPlugin
        items={_mentionItems}
        onMenuOpenChange={handleMentionMenuOpen}
        emptyStateMessage={mentionItemsEmptyMessage}
      />
      <ExtendedTextPlugin noDisplayColor={noDisplayColor || false} themeColors={themeColors} />

      {/* Functions */}
      <MarkdownShortcutPlugin transformers={SUPPORTED_MARKDOWN_TRANSFORMERS} />
      <TabIndentationPlugin />
      <HistoryPlugin />
      {editable && autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}

      {/* Menu */}
      {enableFloatingMenu && (
        <FloatingMenuPlugin
          MenuComponent={FloatingMenu}
          options={{ enableTextAlign, enableExtendedTextFormatting, themeColors, onColorChange }}
        />
      )}

      {/* Handlers */}
      <OnBlurPlugin onBlur={_onBlur} />
      {onFocus && <OnFocusPlugin onFocus={_onFocus} />}
      {editable && debounced && <DebouncedOnChangePlugin onChange={_onChange} ignoreSelectionChange />}
      {editable && !debounced && <OnChangePlugin onChange={_onChange} ignoreSelectionChange />}
      <OnKeyDownPlugin
        onEnter={_onKeyDown.Enter}
        onEscape={_onKeyDown.Escape}
        mentionMenuOpen={mentionMenuOpen}
        propagateArrowNavigation={mentionItems !== undefined}
      />
      {editable && <PlainPastePlugin />}
    </LexicalComposer>
  );
});

export { RichInput };
export default RichInput;
